/**
 * Account Aggregator API Service
 *
 * Provides typed methods for account aggregator endpoints.
 */

import { httpClient } from './http-client.service';
import type {
  AccountAggregatorListResponse,
  AccountAggregatorQueryParams,
  UpdateAccountAggregatorPayload,
  GenerateAuthKeyPayload,
  GenerateAuthKeyResponse,
  UpdateAuthKeyPayload,
  RegisterAccountAggregatorPayload,
} from '../types/account-aggregator';

class AccountAggregatorAPIService {
  async getAccountAggregators(
    params: AccountAggregatorQueryParams
  ): Promise<AccountAggregatorListResponse> {
    try {
      const response = await httpClient.post<AccountAggregatorListResponse>(
        'accountaggregatordetailslist',
        params
      );
      return response.data;
    } catch (error) {
      console.error('[AccountAggregatorAPI] Failed to fetch aggregators:', error);
      throw error;
    }
  }

  async updateAccountAggregator(
    payload: UpdateAccountAggregatorPayload
  ): Promise<any> {
    try {
      const response = await httpClient.post('update/AA', payload);
      return response.data;
    } catch (error) {
      console.error('[AccountAggregatorAPI] Failed to update aggregator:', error);
      throw error;
    }
  }

  async generateAuthKey(
    payload: GenerateAuthKeyPayload
  ): Promise<GenerateAuthKeyResponse> {
    try {
      const response = await httpClient.post<GenerateAuthKeyResponse>(
        'createAPIKey',
        payload
      );
      return response.data;
    } catch (error) {
      console.error('[AccountAggregatorAPI] Failed to generate auth key:', error);
      throw error;
    }
  }

  async updateAuthKeyOutbound(
    payload: UpdateAuthKeyPayload
  ): Promise<any> {
    try {
      const response = await httpClient.post('updateAuthKeyOutbound', payload);
      return response.data;
    } catch (error) {
      console.error('[AccountAggregatorAPI] Failed to update outbound key:', error);
      throw error;
    }
  }

  async registerAccountAggregator(
    payload: RegisterAccountAggregatorPayload
  ): Promise<any> {
    try {
      const response = await httpClient.post('registeraa', payload);
      return response.data;
    } catch (error) {
      console.error('[AccountAggregatorAPI] Failed to register aggregator:', error);
      throw error;
    }
  }

  async updateAggregatorStatus(
    payload: { data: { uuid: string; status: number } }
  ): Promise<any> {
    try {
      const response = await httpClient.post('updateaastatus', payload);
      return response.data;
    } catch (error) {
      console.error('[AccountAggregatorAPI] Failed to update aggregator status:', error);
      throw error;
    }
  }

  /**
   * Fetch all account aggregators without pagination filters.
   * Useful for dropdowns (e.g., create consent drawer).
   */
  async listAllAggregators(): Promise<AccountAggregatorListResponse> {
    try {
      const response = await httpClient.post<AccountAggregatorListResponse>(
        'accountaggregatordetailslist',
        {}
      );
      return response.data;
    } catch (error) {
      console.error('[AccountAggregatorAPI] Failed to fetch aggregator list:', error);
      throw error;
    }
  }
}

export const accountAggregatorAPI = new AccountAggregatorAPIService();
